import { Skeleton } from '@mantine/core';
import { FileText, LayoutDashboard, type LucideIcon, Plug } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

import { AppRoute } from '@/constants/app-routes.ts';
import { FeatureFlag } from '@/constants/feature-flags.ts';

import { useFeatureFlag } from '@/hooks/use-feature-flag.ts';

import { NavLink } from './nav-link.tsx';

interface NavigationLink {
    label: string;
    icon: LucideIcon;
    path: string;
    flag?: FeatureFlag;
}

interface Props {
    link: NavigationLink;
    isActive: boolean;
}

const NavigationLinkItem = ({ link, isActive }: Props) => {
    const navigate = useNavigate();

    const handleClick = (): void => {
        navigate(link.path);
    };

    const Icon = link.icon;

    return (
        <NavLink onClick={handleClick} active={isActive} leftSection={<Icon className="size-5" />} label={link.label} />
    );
};

export const HeaderNavigation = () => {
    const location = useLocation();
    const recall = useFeatureFlag(FeatureFlag.IS_RECALL_ENABLED);
    const connect = useFeatureFlag(FeatureFlag.IS_CONNECT_ENABLED);
    const patientView = useFeatureFlag(FeatureFlag.IS_PATIENTVIEW_ENABLED);

    const flagsLoading = recall.loading || connect.loading || patientView.loading;

    const enabledByFlag: Partial<Record<FeatureFlag, boolean>> = {
        [FeatureFlag.IS_RECALL_ENABLED]: recall.value,
        [FeatureFlag.IS_CONNECT_ENABLED]: connect.value,
        [FeatureFlag.IS_PATIENTVIEW_ENABLED]: patientView.value,
    };

    const links: NavigationLink[] = [
        {
            label: 'CareView',
            icon: LayoutDashboard,
            path: AppRoute.CAREVIEW,
        },
        {
            label: 'Recall',
            icon: LayoutDashboard,
            path: AppRoute.RECALL,
            flag: FeatureFlag.IS_RECALL_ENABLED,
        },
        {
            label: 'Connect',
            icon: Plug,
            path: AppRoute.CONNECT,
            flag: FeatureFlag.IS_CONNECT_ENABLED,
        },
        {
            label: 'Patientenblick',
            icon: FileText,
            path: AppRoute.EXTRACTION,
            flag: FeatureFlag.IS_PATIENTVIEW_ENABLED,
        },
    ];

    if (flagsLoading) {
        return (
            <div className="flex items-center gap-2" aria-label="header-navigation-loading" aria-busy="true">
                <Skeleton className="size-5 rounded-full" />
                <Skeleton className="h-4 w-20" />
            </div>
        );
    }

    return (
        <div className="flex items-center gap-8">
            {links
                .filter((link) => link.flag === undefined || enabledByFlag[link.flag])
                .map((link) => (
                    <NavigationLinkItem key={link.path} link={link} isActive={location.pathname === link.path} />
                ))}
        </div>
    );
};
